import MasterItemCollection from '../collection/MasterItemCollection';
import ItemDetailUtil from './ItemDetailUtil';

const NORMALIZE_FORM = 'NFKC';
// 数字に挟まれた × x * （NFKC・小文字化の後）。「1000×600」「1000 x 600」をサイズの書式にそろえる
const SIZE_SEPARATOR_PATTERN = /(\d)\s*[×x*]\s*(?=\d)/g;
const SIZE_SEPARATOR_REPLACEMENT = `$1${ItemDetailUtil.SIZE_SEPARATOR}`;
const EDGE_SEPARATOR_END_PATTERN = /(\d)[×*]$/;
const EDGE_SEPARATOR_START_PATTERN = /^[×*](?=\d)/;
const EMPTY_TEXT = '';
const FIELD_JOINER = ' ';
const WORD_SEPARATOR = /\s+/;
const SEARCH_FIELDS: readonly (keyof MasterItemCollection)[] = ['item_name', 'item_part_number', 'item_maker_name'];
const LIST_ITEM_SELECTOR = 'li[data-name]';
const ITEM_ID_ATTRIBUTE = 'data-name';
const INPUT_SELECTOR = '.itemSearchInput';
const EMPTY_MESSAGE_SELECTOR = '.itemSearchEmpty';
const ACTIVE_CLASS = 'is-active';
const HIDDEN_DISPLAY = 'none';
const VISIBLE_DISPLAY = '';
const EVENT_INPUT = 'input';
const EVENT_COMPOSITION_START = 'compositionstart';
const EVENT_COMPOSITION_END = 'compositionend';
const EVENT_KEYDOWN = 'keydown';
const EVENT_KEYUP = 'keyup';
const EVENT_CLICK = 'click';
const ESCAPE_KEY = 'Escape';
const SEARCH_BUTTON_SELECTOR = '.itemSearchBtn';
const SEARCH_BOX_HTML = '<div class="itemSearchWrap"><input type="search" class="itemSearchInput" placeholder="フリーワード検索"><button type="button" class="itemSearchBtn"></button></div>';
const EMPTY_MESSAGE_HTML = '<div class="itemSearchEmpty">該当するアイテムがありません。</div>';

/**
 * アイテム一覧のフリーワード検索
 * 対象：名称・品番・メーカー（サイズを吹き出しに出すダイアログではサイズも）
 */
export default class ItemSearchUtil {
  /**
   * 検索欄のHTML
   */
  public static createSearchBoxHtml(): string {
    return SEARCH_BOX_HTML;
  }

  /**
   * 0件表示のHTML
   */
  public static createEmptyMessageHtml(): string {
    return EMPTY_MESSAGE_HTML;
  }

  /**
   * 全角半角・大文字小文字の違いを吸収する
   */
  public static normalize(text: string): string {
    return String(text ?? EMPTY_TEXT).normalize(NORMALIZE_FORM).toLowerCase();
  }

  /**
   * 空白区切りの全語を含むアイテムのitem_idを返す（includeSize ならサイズも対象にする）
   */
  public static filter(items: MasterItemCollection[], query: string, includeSize: boolean = false): Set<string> {
    // サイズを対象にしないダイアログは、区切りをそろえず従来どおりに照合する
    const normalizedQuery: string = includeSize ? ItemSearchUtil.normalizeForMatch(query) : ItemSearchUtil.normalize(query);
    const words: readonly string[] = normalizedQuery.split(WORD_SEPARATOR).filter((word) => word.length > 0)
      .map((word) => (includeSize ? ItemSearchUtil.normalizeEdgeSeparator(word) : word));
    const matched: Set<string> = new Set<string>();
    items.forEach((item) => {
      const fields: string[] = SEARCH_FIELDS.map((field) => String(item[field] ?? EMPTY_TEXT));
      // サイズが無い（0*0*0）アイテムまで「0」で当たらないよう、サイズを持つものだけ加える
      if (includeSize && ItemDetailUtil.hasSize(item)) fields.push(ItemDetailUtil.formatSize(item));
      // 区切りをそろえる前の形も並べ、品番の「2x4」に「x4」でも当たるようにする
      const texts: string[] = fields.map(ItemSearchUtil.normalize);
      if (includeSize) texts.push(...fields.map(ItemSearchUtil.normalizeForMatch));
      const target: string = texts.join(FIELD_JOINER);
      if (words.every((word) => target.indexOf(word) > -1)) matched.add(String(item.item_id));
    });
    return matched;
  }

  /**
   * 一覧の表示を検索結果で切り替え、0件なら文言を出す（一致したitem_idを返す）
   */
  public static applyToList(root: Element, items: MasterItemCollection[], query: string, includeSize: boolean = false): Set<string> {
    const matched: Set<string> = ItemSearchUtil.filter(items, query, includeSize);
    let visibleCount: number = 0;
    root.querySelectorAll(LIST_ITEM_SELECTOR).forEach((elem) => {
      const listItem: HTMLElement = elem as HTMLElement;
      const isVisible: boolean = matched.has(listItem.getAttribute(ITEM_ID_ATTRIBUTE));
      listItem.style.display = isVisible ? VISIBLE_DISPLAY : HIDDEN_DISPLAY;
      if (isVisible) visibleCount += 1;
    });
    root.querySelector(EMPTY_MESSAGE_SELECTOR)?.classList.toggle(ACTIVE_CLASS, visibleCount === 0 && query.trim().length > 0);
    return matched;
  }

  /**
   * 照合用の正規化。品番の「2x4」等も同じ変換を受けるよう、入力と対象の両方に当てる
   */
  private static normalizeForMatch(text: string): string {
    return ItemSearchUtil.normalize(text).replace(SIZE_SEPARATOR_PATTERN, SIZE_SEPARATOR_REPLACEMENT);
  }

  /**
   * 入力途中の「1000×」「×600」用に、数字と隣り合う語の端の × をそろえる
   * 英字の x は品番の頭の x を壊さないよう変換しない
   */
  private static normalizeEdgeSeparator(word: string): string {
    return word.replace(EDGE_SEPARATOR_END_PATTERN, SIZE_SEPARATOR_REPLACEMENT).replace(EDGE_SEPARATOR_START_PATTERN, ItemDetailUtil.SIZE_SEPARATOR);
  }

  /**
   * 検索欄の入力を監視する（IME変換中は確定まで待つ）
   */
  public static bindInput(root: Element, onSearch: (query: string) => void): void {
    const input: HTMLInputElement = root.querySelector(INPUT_SELECTOR);
    if (!input) return;
    let isComposing: boolean = false;
    input.addEventListener(EVENT_COMPOSITION_START, () => { isComposing = true; });
    input.addEventListener(EVENT_COMPOSITION_END, () => {
      isComposing = false;
      onSearch(input.value);
    });
    input.addEventListener(EVENT_INPUT, () => {
      if (!isComposing) onSearch(input.value);
    });
    // 2Dのアイテム削除や3Dの視点移動など、画面全体のキー操作に入力を渡さない
    // Escはダイアログを閉じるため通す（IME変換の取り消しでは閉じないよう止める）
    const stopKeyPropagation = (e: KeyboardEvent): void => {
      if (e.key !== ESCAPE_KEY || e.isComposing) e.stopPropagation();
    };
    input.addEventListener(EVENT_KEYDOWN, stopKeyPropagation);
    input.addEventListener(EVENT_KEYUP, stopKeyPropagation);
    root.querySelector(SEARCH_BUTTON_SELECTOR)?.addEventListener(EVENT_CLICK, () => {
      input.focus();
      onSearch(input.value);
    });
  }

  /**
   * 検索欄の現在の入力値
   */
  public static getQuery(root: Element): string {
    return (root.querySelector(INPUT_SELECTOR) as HTMLInputElement)?.value ?? EMPTY_TEXT;
  }

  /**
   * 紐付け中のアイテムを一覧の先頭へ移す
   */
  public static moveToFront(items: MasterItemCollection[], itemId: string): void {
    if (!itemId) return;
    const index: number = items.findIndex((item) => String(item.item_id) === String(itemId));
    if (index > 0) items.unshift(items.splice(index, 1)[0]);
  }
}
